/**
 * useLogger Hook
 * React hook for easy logging integration in components
 * Following React best practices and SOLID principles
 * 
 * @module useLogger
 */

import { useCallback, useMemo } from 'react';
import { loggingService } from '../logging/loggingService';
import { LOG_STATUSES } from '../logging/LoggingConstants';

/**
 * Custom hook for logging actions in React components
 * Provides a memoized log function that integrates with the logging service
 * 
 * @returns {Object} Logger object with log method
 * @example
 * ```tsx
 * const { log } = useLogger();
 * 
 * const handleSubmit = () => {
 *   log('PHONE_VERIFICATION', LOG_STATUSES.SUCCESS, { accountId: 123 });
 * };
 * ```
 */
const useLogger = () => {
    /**
     * Log an action
     * Memoized to prevent unnecessary re-renders
     * 
     * @param {string} actionName - Action type (from LoggingConstants)
     * @param {string} actionStatus - Action status (SUCCESS, FAILURE, CANCELLED, PENDING)
     * @param {Object} context - Additional context/metadata
     * @returns {void}
     */
    const log = useCallback((actionName: string, actionStatus: string = LOG_STATUSES.SUCCESS, context: Record<string, any> = {}) => {
        // Validate inputs
        if (!actionName || typeof actionName !== 'string') {
            if (process.env.NODE_ENV === 'development') {
                console.warn('useLogger: actionName must be a non-empty string');
            }
            return;
        }
        
        if (context && typeof context !== 'object') {
            if (process.env.NODE_ENV === 'development') {
                console.warn('useLogger: context must be an object');
            }
            return;
        }
        
        loggingService.log(actionName, actionStatus, context || {});
    }, []);

    // Memoize the return value to prevent unnecessary re-renders
    return useMemo(() => ({ log }), [log]);
};

export default useLogger;

